import React, { useState } from "react";
import { Complaint, StatusUpdate } from "../interfaces/Complaint";
import complaintService from "../services/complaint.service";
import { User } from "../interfaces/User";

interface UpdateStatusModalProps {
  selectedComplaint: Complaint;
  statusUpdate: StatusUpdate;
  loading: boolean;
  onClose: () => void;
  onStatusChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onUpdateTextChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSubmit: (e: React.FormEvent) => Promise<void>;
  setRefreshFlag: React.Dispatch<React.SetStateAction<boolean>>;
}

const UpdateStatusModal: React.FC<UpdateStatusModalProps> = ({
  selectedComplaint,
  statusUpdate,
  loading,
  onClose,
  onStatusChange,
  onUpdateTextChange,
  onSubmit,
  setRefreshFlag
}) => {
  const [error, setError] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState<boolean>(false);
  const [rejectLoading, setRejectLoading] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const handleAuthorizeComplaint = async (
    id: string,
    newStatus: string,
    action: "authorize" | "reject"
  ): Promise<void> => {
    try {
      setError(null);
      if (action === "authorize") setAuthLoading(true);
      else if (action === "reject") setRejectLoading(true);
      await complaintService.authorizeComplaint(id, newStatus);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || `Failed to ${action} complaint`;
      setError(errorMessage);
      console.error(`${action} error:`, err);
    } finally {
      if (action === "authorize") setAuthLoading(false);
      else if (action === "reject") setRejectLoading(false);
      onClose();
      setRefreshFlag((prev: boolean) => !prev); 
    }
  };

  const statusOptions = [
    { value: "Pending", label: "Pending" },
    { value: "Ongoing", label: "Ongoing" },
    { value: "Completed", label: "Completed" },
  ];

  const renderButtonContent = (text: string, isLoading: boolean) =>
    isLoading ? (
      <>
        <span
          className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] mr-2"
          role="status"
          aria-hidden="true"
        />
      </>
    ) : (
      text
    );

  const resolvedComplaintId = String(
    selectedComplaint.id || selectedComplaint.complain_id || ""
  );

    console.log("user", user);
  return (
    <div
      className="fixed inset-0  z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-scroll pt-5"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-lg bg-white shadow-lg">
        <div className="flex items-center justify-between border-b p-4">
          <h5 className="text-lg font-semibold">Update Complaint Status</h5>
          <button
            type="button"
            className="text-gray-500 hover:text-gray-700"
            onClick={onClose}
            disabled={loading || authLoading || rejectLoading}
            aria-label="Close"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="p-4">
          {error && (
            <div
              className="mb-4 rounded-md bg-red-100 p-3 text-sm text-red-700"
              role="alert"
            >
              {error}
            </div>
          )}
          <form onSubmit={onSubmit}>
            <div className="mb-4">
              <label
                htmlFor="complaintId"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Complaint ID
              </label>
              <input
                type="text"
                id="complaintId"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-500"
                value={selectedComplaint.complain_id || selectedComplaint.id}
                disabled
                aria-describedby="complaintIdHelp"
              />
              <p id="complaintIdHelp" className="mt-1 text-xs text-gray-500">
                Unique identifier for the complaint.
              </p>
            </div>
            <div className="mb-4">
              <label
                htmlFor="complaintSubject"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Subject
              </label>
              <input
                type="text"
                id="complaintSubject"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-500"
                value={selectedComplaint.subject}
                disabled
                aria-describedby="complaintSubjectHelp"
              />
              <p
                id="complaintSubjectHelp"
                className="mt-1 text-xs text-gray-500"
              >
                Subject of the complaint.
              </p>
            </div>
            <div className="mb-4">
              <label
                htmlFor="statusSelect"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Status
              </label>
              <select
                id="statusSelect"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                value={statusUpdate.updated_status}
                onChange={onStatusChange}
                disabled={loading || authLoading || rejectLoading}
                aria-describedby="statusSelectHelp"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p id="statusSelectHelp" className="mt-1 text-xs text-gray-500">
                Select the new status for the complaint.
              </p>
            </div>
            <div className="mb-4">
              <label
                htmlFor="updateNotes"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Update Notes
              </label>
              <textarea
                id="updateNotes"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                rows={4}
                value={statusUpdate.update_text}
                onChange={onUpdateTextChange}
                placeholder="Provide details about this status update..."
                required
                disabled={loading || authLoading || rejectLoading}
                aria-describedby="updateNotesHelp"
              />
              <p id="updateNotesHelp" className="mt-1 text-xs text-gray-500">
                Additional details for the status update.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-blue-400"
                disabled={loading || authLoading || rejectLoading}
              >
                {renderButtonContent("Update Status", loading)}
              </button>

              {selectedComplaint.isAuthorized != "1" && (user?.role_id == 3 || user?.role_id == 5) && (
                <button
                  type="button"
                  className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:bg-green-400"
                  onClick={() =>
                    handleAuthorizeComplaint(
                      resolvedComplaintId,
                      "1",
                      "authorize"
                    )
                  }
                  disabled={loading || authLoading || rejectLoading || !resolvedComplaintId}
                >
                  {renderButtonContent("Authorize", authLoading)}
                </button>
              )}

              {selectedComplaint.isAuthorized != "5" && (user?.role_id == 3 || user?.role_id == 5) &&(
                <button
                  type="button"
                  className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:bg-red-400"
                  onClick={() =>
                    handleAuthorizeComplaint(
                      resolvedComplaintId,
                      "5",
                      "reject"
                    )
                  }
                  disabled={loading || authLoading || rejectLoading || !resolvedComplaintId}
                >
                  {renderButtonContent("Reject", rejectLoading)}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdateStatusModal;
